const { getElements, getElement } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  availability: '[data-product-availability]',
  form: '[data-product-form]',
  optionTrigger: '[data-product-option-trigger]',
  price: '[data-product-price]',
  sku: '[data-product-sku]',
  variantIndicator: '[data-product-variant-indicator]',
  backInStock: '[data-product-back-in-stock]',
  makeSelection: '[data-product-make-selection]',
  viewSimilar: '[data-product-view-similar]',
  variantButtonText: '[data-product-variant-button-text]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  async setPrice () {
    await window.BAO.sectionRenderer
      .fetch(
        'product-price',
        `${this.url.toString()}?variant=${this.data.id}&section_id=product-price`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-price'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        const priceElements = getElements(selectors.price)
        priceElements.forEach((el) => {
          el.innerHTML = myself.innerHTML
        })
      })
  }

  setAvailability (event, context) {
    getElements(
      `${selectors.makeSelection}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.setAttribute('aria-hidden', true)
    })

    getElements(
      `${selectors.addToCart}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.disabled = !this.data.available
      el.setAttribute('aria-hidden', !this.data.available)
    })

    // If the option has the discontinued attribute, the product should show
    // the 'View Similar' button instead of the 'Notify Me' button.
    const optionDiscontinued = event.target.dataset.discontinued === 'true'

    getElements(
      `${selectors.backInStock}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.setAttribute(
        'aria-hidden',
        this.data.available || optionDiscontinued === true,
      )
      el.setAttribute(
        'data-product-url',
        `${this.url.toString()}?variant=${this.data.id}`,
      )
    })

    getElements(
      `${selectors.viewSimilar}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.dataset.size = this.data.option1
      el.setAttribute(
        'aria-hidden',
        this.data.available || optionDiscontinued !== true,
      )
    })

    let addToCartText = this.data.available
      ? window.theme.strings.addToCart
      : window.theme.strings.soldOut

    const form = document.getElementById(context.dataset.formId)
    const preorderConfig = form?.querySelector?.(
      '[data-product-preorder-config]',
    )
    if (
      preorderConfig?.dataset?.preorderEnabled === 'true' &&
      window.theme.strings.preOrder
    ) {
      const switchDate = preorderConfig.dataset.preorderSwitchDate

      if (!switchDate) {
        addToCartText = window.theme.strings.preOrder
      } else {
        const now = Date.now() // milliseconds
        const switchTimestamp = Number(switchDate) * 1000 // seconds → ms

        if (now < switchTimestamp) {
          addToCartText = window.theme.strings.preOrder
        }
      }
    }
    const isPreorderCta = addToCartText === window.theme.strings.preOrder
    getElements(
      `${selectors.addToCartText}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.textContent = addToCartText
      const btn = el.closest?.('[data-product-add-to-cart]')
      if (btn) {
        btn.classList.toggle('btn-Button-preorder', isPreorderCta)
      }
    })

    getElements(selectors.availability).forEach((el) => {
      el.setAttribute('aria-hidden', false)
      el.classList.toggle('prd-Detail_Stock-in', this.data.available)
    })

    getElements(
      `[data-product-preorder-message][data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.setAttribute('aria-hidden', !this.data.available)
    })
  }

  updateInput (context) {
    const inputs = getElements(
      `form[action*="/cart/add"][id="${context.dataset.formId}"] [name="id"]`,
    )

    inputs.forEach((input) => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateOptionTriggers (name, value, context) {
    const allOptionTriggers = getElements(
      `${selectors.optionTrigger}[data-form='${context.dataset.formId}']`,
    )

    if (!allOptionTriggers) return

    const optionTriggers = allOptionTriggers.filter(
      (el) => el.dataset.name === name,
    )

    if (!optionTriggers) return

    optionTriggers.forEach((el) => {
      el.setAttribute('aria-pressed', el.dataset.value === value)
    })
  }

  updateIndicator (name, markup, displayTimeMs) {
    if (!name || !markup) return

    const allVariantIndicators = getElements(selectors.variantIndicator)
    const variantIndicators = allVariantIndicators.filter(
      (el) => el.dataset.name === name,
    )

    if (!variantIndicators) return

    variantIndicators.forEach((el) => {
      const defaultMarkup = el.innerHTML

      el.innerHTML = markup
      el.classList.remove('prd-Detail_VariantText-light')

      if (displayTimeMs) {
        setTimeout(() => {
          el.innerHTML = defaultMarkup
          el.classList.add('prd-Detail_VariantText-light')
        }, displayTimeMs)
      }
    })
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange (e) {
    const target = e.target
    const variant = this.getVariant()
    const variantButtonText = document.querySelector(
      `label[for="${target.id}"] ${selectors.variantButtonText}`,
    )?.innerHTML

    variant.updateInput(this)

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    if (target.dataset.variantRadiosTriggerEventOnChange === 'true') {
      this.triggerEventChange(target)
    }

    if (variant.data?.available && target.name && variantButtonText) {
      variant.updateIndicator(target.name, variantButtonText, 5000)
    }
  }

  triggerEventChange (target) {
    const productFormId = target.dataset.variantRadiosFormId

    if (productFormId !== null) {
      const form = getElement(`#${productFormId}`)

      if (form !== null && this.currentVariant !== null) {
        form.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: {
              data: {
                variant: this.currentVariant.name,
              },
            },
          }),
        )
      }
    }
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find((el) => el.checked).value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}
